<script setup lang="ts">
import VideoCard from './VideoCard.vue';
import type { VideoEntry } from '../types';
import { useVideoStore } from '../stores/videoStore';

const videoStore = useVideoStore();

defineProps<{
  videos: VideoEntry[];
}>();

const emit = defineEmits<{
  play: [id: string, name: string, segmented: boolean];
  delete: [id: string];
  select: [id: string];
}>();
</script>

<template>
  <div class="video-list">
    <VideoCard
      v-for="video in videos"
      :key="video.id"
      :video="video"
      :selected="videoStore.selectedIds.has(video.id)"
      @play="(id) => emit('play', id, video.name, !!video.segmented)"
      @delete="emit('delete', $event)"
      @select="emit('select', $event)"
    />
  </div>
</template>

<style scoped>
.video-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 32px;
  width: 100%;
}

:deep(.video-card) {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  padding: 0 16px;
  animation: fadeIn 0.3s ease-out both;
  width: 100%;
  position: relative;
}

:deep(.select-checkbox) {
  position: relative !important;
  top: auto !important;
  left: auto !important;
  flex-shrink: 0;
  margin: 0;
}

:deep(.thumb-wrapper) {
  width: 180px;
  min-width: 180px;
  aspect-ratio: 16/9;
  flex-shrink: 0;
}

:deep(.card-body) {
  flex: 1;
  min-width: 0;
  padding: 12px 16px 12px 0;
}

:deep(.name) {
  font-size: 15px;
  font-weight: 500;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

:deep(.meta) { font-size: 13px; }

@media (max-width: 768px) {
  .video-list {
    gap: 8px;
    padding: 0 16px;
    width: 100%;
  }

  :deep(.video-card) {
    gap: 10px;
    width: 100%;
  }

  :deep(.thumb-wrapper) {
    width: 120px;
    min-width: 120px;
  }

  :deep(.card-body) {
    padding: 10px 12px 10px 0;
  }

  :deep(.name) {
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  :deep(.thumb-wrapper) {
    width: 100px;
    min-width: 100px;
  }

  :deep(.name) {
    font-size: 13px;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    display: -webkit-box;
    white-space: normal;
  }

  :deep(.meta) {
    font-size: 11px;
  }
}
</style>
